import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createClient } from '@supabase/supabase-js';
import toast from 'react-hot-toast';

// Types
export interface LegacyItem {
  id: string;
  name: string;
  description: string;
  category: string;
  imageUrl?: string;
  mintDate: string;
  blockchain: string;
  tokenId?: string;
  ownerAddress: string;
  familyMembers?: string[];
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface LegacyCollection {
  id: string;
  name: string;
  description: string;
  items: LegacyItem[];
  createdAt: string;
  updatedAt: string;
}

interface LegacyContextType {
  items: LegacyItem[];
  collections: LegacyCollection[];
  isLoading: boolean;
  error: string | null;
  addItem: (item: Omit<LegacyItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateItem: (id: string, updates: Partial<LegacyItem>) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  getItem: (id: string) => LegacyItem | undefined;
  getItemsByOwner: (ownerAddress: string) => LegacyItem[];
  getItemsByCategory: (category: string) => LegacyItem[];
  shareWithFamily: (itemId: string, familyMemberAddress: string) => Promise<void>;
  addCollection: (collection: Omit<LegacyCollection, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateCollection: (id: string, updates: Partial<LegacyCollection>) => Promise<void>;
  deleteCollection: (id: string) => Promise<void>;
  getCollection: (id: string) => LegacyCollection | undefined;
}

// Supabase client with fallback
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

let supabase: any = null;

if (supabaseUrl && supabaseKey) {
  try {
    supabase = createClient(supabaseUrl, supabaseKey);
  } catch (error) {
    console.warn('Failed to initialize Supabase client:', error);
  }
} else {
  console.warn('Missing Supabase environment variables - using mock data');
}

// Context
const LegacyContext = createContext<LegacyContextType | undefined>(undefined);

// Provider
export const LegacyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<LegacyItem[]>([]);
  const [collections, setCollections] = useState<LegacyCollection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load items on mount
  useEffect(() => {
    loadItems();
    loadCollections();
  }, []);

  const loadItems = async () => {
    try {
      setIsLoading(true);
      
      if (!supabase) {
        console.log('Using mock data - Supabase not available');
        setItems([]);
        return;
      }

      const { data, error } = await supabase
        .from('legacy_items')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error loading items:', err);
      setError('Failed to load items');
      toast.error('Failed to load items');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCollections = async () => {
    try {
      if (!supabase) {
        console.log('Using mock data - Supabase not available');
        setCollections([]);
        return;
      }

      const { data, error } = await supabase
        .from('legacy_collections')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCollections(data || []);
    } catch (err) {
      console.error('Error loading collections:', err);
      setError('Failed to load collections');
    }
  };

  const addItem = async (item: Omit<LegacyItem, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (!supabase) {
        toast.error('Database not available');
        return;
      }

      const { data, error } = await supabase
        .from('legacy_items')
        .insert([item])
        .select()
        .single();

      if (error) throw error;
      
      setItems(prev => [data, ...prev]);
      toast.success('Item added successfully!');
    } catch (err) {
      console.error('Error adding item:', err);
      toast.error('Failed to add item');
    }
  };

  const updateItem = async (id: string, updates: Partial<LegacyItem>) => {
    try {
      if (!supabase) {
        toast.error('Database not available');
        return;
      }

      const { data, error } = await supabase
        .from('legacy_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setItems(prev => prev.map(item => item.id === id ? data : item));
      toast.success('Item updated successfully!');
    } catch (err) {
      console.error('Error updating item:', err);
      toast.error('Failed to update item');
    }
  };

  const deleteItem = async (id: string) => {
    try {
      if (!supabase) {
        toast.error('Database not available');
        return;
      }

      const { error } = await supabase
        .from('legacy_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setItems(prev => prev.filter(item => item.id !== id));
      toast.success('Item deleted successfully!');
    } catch (err) {
      console.error('Error deleting item:', err);
      toast.error('Failed to delete item');
    }
  };

  const getItem = (id: string) => {
    return items.find(item => item.id === id);
  };

  const getItemsByOwner = (ownerAddress: string) => {
    return items.filter(item => item.ownerAddress === ownerAddress);
  };

  const getItemsByCategory = (category: string) => {
    return items.filter(item => item.category === category);
  };

  const shareWithFamily = async (itemId: string, familyMemberAddress: string) => {
    try {
      if (!supabase) {
        toast.error('Database not available');
        return;
      }

      const item = getItem(itemId);
      if (!item) {
        toast.error('Item not found');
        return;
      }

      const updatedFamilyMembers = [...(item.familyMembers || []), familyMemberAddress];
      
      const { error } = await supabase
        .from('legacy_items')
        .update({ familyMembers: updatedFamilyMembers })
        .eq('id', itemId);

      if (error) throw error;
      
      setItems(prev => prev.map(item => 
        item.id === itemId 
          ? { ...item, familyMembers: updatedFamilyMembers }
          : item
      ));
      
      toast.success('Item shared with family member!');
    } catch (err) {
      console.error('Error sharing item:', err);
      toast.error('Failed to share item');
    }
  };

  const addCollection = async (collection: Omit<LegacyCollection, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (!supabase) {
        toast.error('Database not available');
        return;
      }

      const { data, error } = await supabase
        .from('legacy_collections')
        .insert([collection])
        .select()
        .single();

      if (error) throw error;
      
      setCollections(prev => [data, ...prev]);
      toast.success('Collection added successfully!');
    } catch (err) {
      console.error('Error adding collection:', err);
      toast.error('Failed to add collection');
    }
  };

  const updateCollection = async (id: string, updates: Partial<LegacyCollection>) => {
    try {
      if (!supabase) {
        toast.error('Database not available');
        return;
      }

      const { data, error } = await supabase
        .from('legacy_collections')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setCollections(prev => prev.map(collection => collection.id === id ? data : collection));
      toast.success('Collection updated successfully!');
    } catch (err) {
      console.error('Error updating collection:', err);
      toast.error('Failed to update collection');
    }
  };

  const deleteCollection = async (id: string) => {
    try {
      if (!supabase) {
        toast.error('Database not available');
        return;
      }

      const { error } = await supabase
        .from('legacy_collections')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setCollections(prev => prev.filter(collection => collection.id !== id));
      toast.success('Collection deleted successfully!');
    } catch (err) {
      console.error('Error deleting collection:', err);
      toast.error('Failed to delete collection');
    }
  };

  const getCollection = (id: string) => {
    return collections.find(collection => collection.id === id);
  };

  const value = {
    items,
    collections,
    isLoading,
    error,
    addItem,
    updateItem,
    deleteItem,
    getItem,
    getItemsByOwner,
    getItemsByCategory,
    shareWithFamily,
    addCollection,
    updateCollection,
    deleteCollection,
    getCollection,
  };

  return (
    <LegacyContext.Provider value={value}>
      {children}
    </LegacyContext.Provider>
  );
};

export const useLegacy = () => {
  const context = useContext(LegacyContext);
  if (context === undefined) {
    throw new Error('useLegacy must be used within a LegacyProvider');
  }
  return context;
}; 